import { resolveBaseConfig, resolveEffectiveBaseConfig } from '@/workflow/_pure/config/configResolution';
import { assertAuthoredRunnerPresentation } from '@/workflow-runner/_pure/config/presentation';
import { CONFIG as RUNNER_WORKFLOW_CONFIG } from '@/workflow-runner/_data/config/config';
import { CONFIG as BUILDER_WORKFLOW_CONFIG } from '@/workflow-builder/_data/config';
import { DEFAULT_MODE_CONFIG } from '@/workflow/modes/_@shared/_data/configRegistry';
import type {
    Config,
    AuthoredBaseConfig,
    NonActiveDimmingConfig,
    OperationPresentationMode,
} from '@/workflow/_contract/config';
import type { AdapterMode } from '@/workflow/_contract/mode';
import type { ModeConfig as BuilderModeConfig } from '@/workflow-builder/_contract/config';
import type { AuthoredModeConfig, ModeConfig as RunnerModeConfig } from '@/workflow-runner/_contract/config';
import type { RegisteredConfig } from '@/workflow/modes/_@shared/_contract/registry';

function resolveNonActiveDimming<T extends RunnerModeConfig['nonActiveDimming']>(
    defaults: T,
    input: Partial<T> | undefined,
): T {
    return {
        ...defaults,
        ...input,
        enabledByConstellationKey: {
            ...(defaults.enabledByConstellationKey ?? {}),
            ...(input?.enabledByConstellationKey ?? {}),
        },
        opacityByConstellationKey: {
            ...(defaults.opacityByConstellationKey ?? {}),
            ...(input?.opacityByConstellationKey ?? {}),
        },
        enabledByLayoutKind: {
            ...(defaults.enabledByLayoutKind ?? {}),
            ...(input?.enabledByLayoutKind ?? {}),
        },
        opacityByLayoutKind: {
            ...(defaults.opacityByLayoutKind ?? {}),
            ...(input?.opacityByLayoutKind ?? {}),
        },
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOptionalNumber(record: Record<string, unknown>, key: string): boolean {
    return record[key] === undefined || typeof record[key] === 'number';
}

function hasOptionalBoolean(record: Record<string, unknown>, key: string): boolean {
    return record[key] === undefined || typeof record[key] === 'boolean';
}

function hasOptionalRecord(
    record: Record<string, unknown>,
    key: string,
    predicate: (value: Record<string, unknown>) => boolean = () => true,
): boolean {
    const value = record[key];
    return value === undefined || (isRecord(value) && predicate(value));
}

function isPartialNonActiveDimming(value: unknown): value is NonActiveDimmingConfig {
    if (!isRecord(value)) return false;
    const hasBooleanValues = (record: Record<string, unknown>) =>
        Object.values(record).every((entry) => typeof entry === 'boolean');
    const hasNumberValues = (record: Record<string, unknown>) =>
        Object.values(record).every((entry) => typeof entry === 'number');
    return hasOptionalNumber(value, 'defaultOpacity')
        && hasOptionalRecord(value, 'enabledByConstellationKey', hasBooleanValues)
        && hasOptionalRecord(value, 'enabledByLayoutKind', hasBooleanValues)
        && hasOptionalRecord(value, 'opacityByConstellationKey', hasNumberValues)
        && hasOptionalRecord(value, 'opacityByLayoutKind', hasNumberValues);
}

function isPartialBuilderConfig(value: unknown): value is Partial<BuilderModeConfig> {
    if (!isRecord(value)) return false;
    return hasOptionalRecord(value, 'nonActiveDimming', isPartialNonActiveDimming)
        && hasOptionalRecord(value, 'workflowStateEntries', (workflowStateEntries) =>
            workflowStateEntries.operationPresentationMode === undefined
            || workflowStateEntries.operationPresentationMode === 'panels'
            || workflowStateEntries.operationPresentationMode === 'wheels');
}

function isPartialRunnerConfig(value: unknown): value is Partial<RunnerModeConfig> {
    if (!isRecord(value)) return false;
    return hasOptionalNumber(value, 'standoffNearFraction')
        && hasOptionalNumber(value, 'standoffFarFraction')
        && hasOptionalRecord(value, 'traceSelection', (traceSelection) =>
            (traceSelection.workflowTraceSelectionKind === undefined
                || traceSelection.workflowTraceSelectionKind === 'latest-by-timestamp'
                || traceSelection.workflowTraceSelectionKind === 'by-handle')
            && (traceSelection.workflowTraceHandle === undefined
                || typeof traceSelection.workflowTraceHandle === 'string')
            && hasOptionalNumber(traceSelection, 'replayPositionIndex'))
        && hasOptionalRecord(value, 'playback', (playback) =>
            hasOptionalNumber(playback, 'positionIntervalMs')
            && hasOptionalBoolean(playback, 'startPlaying')
            && (playback.naturalLessThanPresentationMode === undefined
                || playback.naturalLessThanPresentationMode === 'boolean-channel'
                || playback.naturalLessThanPresentationMode === 'travelling-eye')
            && hasOptionalRecord(playback, 'loopContinuationAnimation', (animation) =>
                hasOptionalNumber(animation, 'transitionDurationMs')
                && hasOptionalNumber(animation, 'spaceShiftPhaseStart')
                && hasOptionalNumber(animation, 'spaceShiftPhaseEnd'))
            && hasOptionalRecord(playback, 'branchExitRebindAnimation', (animation) =>
                hasOptionalNumber(animation, 'transitionDurationMs')
                && hasOptionalNumber(animation, 'spaceShiftPhaseStart')
                && hasOptionalNumber(animation, 'spaceShiftPhaseEnd'))
            && hasOptionalRecord(playback, 'naturalLessThanTransport', (transport) =>
                hasOptionalNumber(transport, 'naturalRadialEntryDurationMs')
                && hasOptionalNumber(transport, 'naturalRingHingeDurationMs')
                && hasOptionalNumber(transport, 'toolStepRingTravelDurationMs')
                && hasOptionalNumber(transport, 'booleanRingHingeDurationMs')
                && hasOptionalNumber(transport, 'booleanRadialExitDurationMs')
                && hasOptionalNumber(transport, 'settlementDurationMs')))
        && hasOptionalRecord(value, 'nonActiveDimming', isPartialNonActiveDimming)
        && hasOptionalRecord(value, 'workflowStateEntries', (workflowStateEntries) =>
            workflowStateEntries.operationPresentationMode === undefined
            || workflowStateEntries.operationPresentationMode === 'panels'
            || workflowStateEntries.operationPresentationMode === 'wheels');
}

function resolveRunnerConfig(input: Partial<RunnerModeConfig> | undefined): RunnerModeConfig {
    const defaults = RUNNER_WORKFLOW_CONFIG.adapterModeConfig;
    return {
        ...defaults,
        ...input,
        traceSelection: {
            ...defaults.traceSelection,
            ...(input?.traceSelection ?? {}),
        },
        playback: {
            ...defaults.playback,
            ...(input?.playback ?? {}),
            loopContinuationAnimation: {
                ...defaults.playback.loopContinuationAnimation,
                ...(input?.playback?.loopContinuationAnimation ?? {}),
            },
            branchExitRebindAnimation: {
                ...(defaults.playback.branchExitRebindAnimation ?? defaults.playback.loopContinuationAnimation),
                ...(input?.playback?.branchExitRebindAnimation
                    ?? input?.playback?.loopContinuationAnimation
                    ?? {}),
            },
            naturalLessThanTransport: {
                ...defaults.playback.naturalLessThanTransport,
                ...(input?.playback?.naturalLessThanTransport ?? {}),
            },
        },
        nonActiveDimming: resolveNonActiveDimming(defaults.nonActiveDimming, input?.nonActiveDimming),
    };
}

function resolveModeConfig<T>(
    value: unknown,
    isPartialConfig: (value: unknown) => value is Partial<T>,
    resolve: (input: Partial<T> | undefined) => T,
    adapterMode: AdapterMode,
): T {
    if (value === undefined) return resolve(undefined);
    if (!isPartialConfig(value)) {
        throw new Error(`Invalid ${adapterMode} workflow mode config.`);
    }
    return resolve(value);
}

function resolveBuilderConfig(input: Partial<BuilderModeConfig> | undefined): BuilderModeConfig {
    const defaults = BUILDER_WORKFLOW_CONFIG.adapterModeConfig;
    return {
        ...defaults,
        ...input,
        nonActiveDimming: resolveNonActiveDimming(defaults.nonActiveDimming, input?.nonActiveDimming),
    };
}

export type AuthoredWorkflowConfig = Partial<AuthoredBaseConfig> & (
    | { adapterMode: 'runner'; adapterModeConfig?: Partial<AuthoredModeConfig> }
    | { adapterMode: 'builder'; adapterModeConfig?: Partial<BuilderModeConfig> }
);

/** Public authored boundary; effective configs use selectEffectiveConfig instead. */
export function selectConfig(
    config: AuthoredWorkflowConfig | undefined,
    adapterMode: AdapterMode = config?.adapterMode ?? DEFAULT_MODE_CONFIG.adapterMode,
): RegisteredConfig {
    const sharedConfig = resolveBaseConfig(config);
    assertAuthoredRunnerPresentation(config?.adapterModeConfig as Partial<AuthoredModeConfig> | undefined);
    return selectEffectiveConfig(config ? {
        ...sharedConfig,
        adapterMode: config.adapterMode,
        adapterModeConfig: config.adapterModeConfig,
    } : undefined, adapterMode);
}

export function selectEffectiveConfig(
    config: Config | undefined,
    adapterMode: AdapterMode = config?.adapterMode ?? DEFAULT_MODE_CONFIG.adapterMode,
): RegisteredConfig {
    const sharedConfig = resolveEffectiveBaseConfig(config);
    const adapterModeConfig = config?.adapterMode === adapterMode ? config.adapterModeConfig : undefined;

    switch (adapterMode) {
        case 'builder':
            return {
                ...sharedConfig,
                adapterMode,
                adapterModeConfig: resolveModeConfig(
                    adapterModeConfig,
                    isPartialBuilderConfig,
                    resolveBuilderConfig,
                    adapterMode,
                ),
            };
        case 'runner':
            return {
                ...sharedConfig,
                adapterMode,
                adapterModeConfig: resolveModeConfig(
                    adapterModeConfig,
                    isPartialRunnerConfig,
                    resolveRunnerConfig,
                    adapterMode,
                ),
            };
        default:
            throw new Error(`Unsupported Workflow adapter mode: ${adapterMode}`);
    }

}

export function resolveEffectiveOperationPresentationMode(
    config: RegisteredConfig,
): OperationPresentationMode {
    const modeOverride = config.adapterModeConfig.workflowStateEntries?.operationPresentationMode;
    return modeOverride ?? config.domainPresentation.entities.workflowStateEntries.operationPresentationMode;
}
